import { Component, inject } from '@angular/core';
import { FilterType, FilterDataType } from '@src/app/shared/components/table-filter/table-filter.component';
import { Filter, FilterRequest } from '@src/app/shared/backend/clients';
import { Router } from '@angular/router';
import { TableSettingService, Column } from '@src/app/shared/services/table.setting.service';
import { SortColumn, SortConfig } from '@src/app/shared/components/sort-panel/custom-sort-panel.model';
import { loadStoredSortPanelState, persistSortPanelState, clearSortPanelState } from '@src/app/shared/components/sort-panel/sort-panel-storage.helper';

type ColumnWithSortField = Column;

@Component({
    selector: 'app-base-list',
    template: '',
    styles: [],
    standalone: false
})
export class BaseListComponent {
  protected privateRouter = inject(Router);

  protected filterTypes = FilterType;

  protected pageSize: number = 10;
  protected pageNumber: number = 1;

  protected countries: FilterDataType[] = [];
  protected localData: any;

  protected tableColumns: any;
  protected allTableColumns: Column[] = [];

  protected tableName: any;
  protected page: any;

  protected tableSettingService: TableSettingService | null = null;

  protected filter: FilterRequest = {
    pageSize: this.pageSize,
    pageNumber: this.pageNumber
  };

  public sortableColumns: SortColumn[] = [];
  public sortPanelSort: SortConfig | null = null;
  protected defaultSort: SortConfig | null = null;
  protected sortWasClearedByUser = false;

  // PrimeNG sort bindings
  public primeTableSortField: string | null = null;
  public primeTableSortOrder: number = 0;

  // PrimeNG emits onLazyLoad after triggered programmatically sorts
  // bugfix: suppress the next 1-2 events to duplicated API calls
  private suppressedLazyLoadEventsRemaining = 0;

  // bugfix: PrimeNG emits onLazyLoad before initSortPanel finishes
  private sortPanelInitialized = false;

  // bugfix: dedupe identical lazy load sort events
  private lastLazySortEventSignature: string | null = null;
  private lastLazySortEventAtMs: number = 0;

  protected sortPanelStorageTableName: string | null = null;

  protected set data(value: any) {
    this.localData = value;
  }

  protected get data() {
    return this.localData;
  }

  async get() { }

  async setCache(tableName: any = null) {
    // sort storage tableName
    const normalizedTableName = String(tableName ?? '').trim();
    if (normalizedTableName.length > 0) {
      this.sortPanelStorageTableName = normalizedTableName;
    } else {
      this.sortPanelStorageTableName = null;
    }

    const allColumns = this.tableSettingService?.getTableSettings(tableName) ?? [];
    this.allTableColumns = Array.isArray(allColumns) ? allColumns : [];

    this.tableColumns ??= await this.tableSettingService?.getTableDefaults(tableName);
  }

  protected mapUiFieldToApiField(cellField: string): string {
    const normalizedUiField = String(cellField ?? '').trim();

    const colFromAll = this.allTableColumns?.find((c: any) => c.field === normalizedUiField) as ColumnWithSortField | undefined;
    if (colFromAll) {
      return colFromAll.sortField ?? colFromAll.filterField ?? normalizedUiField;
    }

    const colFromVisible = (this.tableColumns?.find((c: any) => c.field === normalizedUiField) as ColumnWithSortField | undefined);
    return colFromVisible?.sortField ?? colFromVisible?.filterField ?? normalizedUiField;
  }

  protected initSortPanel(params: { defaultSort: SortConfig; labelResolver?: (col: Column) => string; }) {

    const visibleTableColumns: Column[] = Array.isArray(this.tableColumns) ? this.tableColumns : [];

    const resolveLabelForColumn =
      params.labelResolver ??
      ((col: Column) => {
        return col.header;
      });

    // 1) Build the "Sort by" dropdowns
    const sortByDropdownOptions: SortColumn[] = [];

    for (const column of visibleTableColumns) {
      if (!column) {
        continue;
      }

      const rawUiField = column.field;
      const uiField = String(rawUiField ?? '').trim();

      const hasValidUiField = uiField.length > 0;
      if (!hasValidUiField) {
        continue;
      }

      const isActionsColumn = uiField.toLowerCase() === 'actions';
      if (isActionsColumn) {
        continue;
      }

      // label shown to the user
      const dropdownLabel = resolveLabelForColumn(column);

      sortByDropdownOptions.push({
        key: uiField,
        label: dropdownLabel
      });
    }

    this.sortableColumns = sortByDropdownOptions;

    // 2) Normalize the default sort
    const rawDefaultKey = params.defaultSort?.key;
    const defaultSortKey = String(rawDefaultKey ?? '').trim();

    const rawDirection = params.defaultSort?.direction;
    let defaultSortDirection: 'ASC' | 'DESC' = 'DESC';

    if (rawDirection === 'ASC') {
      defaultSortDirection = 'ASC';
    }

    this.defaultSort = {
      key: defaultSortKey,
      direction: defaultSortDirection
    };

    // 3) Reset panel state
    this.sortPanelSort = null;
    this.sortWasClearedByUser = false;

    // 4) sort icons management
    let isDefaultSortKeyVisible = false;

    if (defaultSortKey.length > 0) {
      for (const option of this.sortableColumns) {
        if (option.key === defaultSortKey) {
          isDefaultSortKeyVisible = true;
          break;
        }
      }
    }

    if (isDefaultSortKeyVisible) {
      // default sort key is visible -> show icon on that col
      this.primeTableSortField = defaultSortKey;

      const ascendingOrder = 1;
      const descendingOrder = -1;

      if (defaultSortDirection === 'ASC') {
        this.primeTableSortOrder = ascendingOrder;
      } else {
        this.primeTableSortOrder = descendingOrder;
      }
    } else {
      // default sort key is not visible -> do not show sort icon
      this.primeTableSortField = null;
      this.primeTableSortOrder = 0;
    }

    // 4.5) Restore persisted sort if any (overrides default sort state)
    this.restoreSortPanelStateFromStorage();

    // 5) populate this.filter.sorts
    this.applySortToFilterRequest();

    // Prevent duplicate init sort requests when PrimeNG emits lazy-load events for an already bound visible sort
    // (bugfix click 3 times primeNg table header to trigger column sort)
    const shouldSuppressInitialLazyLoadSortEvents =
      typeof this.primeTableSortField === 'string' &&
      this.primeTableSortField.trim().length > 0 &&
      (this.primeTableSortOrder === 1 || this.primeTableSortOrder === -1);

    this.suppressedLazyLoadEventsRemaining = shouldSuppressInitialLazyLoadSortEvents ? 2 : 0; // prevent the 2 init calls to filter-table

    this.sortPanelInitialized = true;
  }

  protected applySortToFilterRequest(): void {
    if (this.sortWasClearedByUser) {
      delete this.filter.sorts;
      return;
    }

    // Sort to apply (sort panel or pagiantion/ initial load)
    const resolvedSort = this.sortPanelSort ?? this.defaultSort;
    if (!resolvedSort) {
      delete this.filter.sorts;
      return;
    }

    this.filter.sorts = [
      {
        propertyName: this.mapUiFieldToApiField(resolvedSort.key),
        type: resolvedSort.direction === 'ASC' ? 'asc' : 'desc' // backend expects lowercase
      }
    ];
  }

  private clearSortingState(): void {
    this.sortPanelSort = null;
    this.sortWasClearedByUser = true;

    // No sort
    this.primeTableSortField = null;
    this.primeTableSortOrder = 0;

    delete this.filter.sorts;
  }

  public async onSortPanelChange(incomingSort: SortConfig | null): Promise<void> {
    this.suppressedLazyLoadEventsRemaining = 2;

    if (incomingSort == null) {
      this.clearSortingState();

      // persist "cleared" state
      this.persistSortPanelStateToStorage();

      this.pageNumber = 1;
      this.filter.pageNumber = 1;

      this.onCriteriaResetRequested();
      await this.get();
      return;
    }

    // User applied a sort
    this.sortPanelSort = incomingSort;
    this.sortWasClearedByUser = false;

    // Sync table bindings -> icons
    this.primeTableSortField = incomingSort.key;
    this.primeTableSortOrder = incomingSort.direction === 'ASC' ? 1 : -1;

    this.applySortToFilterRequest();

    // persist user "applied" sort
    this.persistSortPanelStateToStorage();

    this.pageNumber = 1;
    this.filter.pageNumber = 1;

    this.onCriteriaResetRequested();
    await this.get();
  }

  async customSort(evt: any): Promise<void> {

    if (!this.sortPanelInitialized) {
      return;
    }

    if (this.suppressedLazyLoadEventsRemaining > 0) {
      this.suppressedLazyLoadEventsRemaining = this.suppressedLazyLoadEventsRemaining - 1;
      return;
    }

    let incomingSortField = '';
    let incomingSortOrder: 1 | -1 | null = null;

    const hasSingleSortField = evt && evt.sortField != null;
    const hasMultiSortMeta = evt && Array.isArray(evt.multiSortMeta) && evt.multiSortMeta.length > 0;

    // bugfix: PrimeNG emits load events, if they dont contain sorting info -> ignore sort
    if (!hasSingleSortField && !hasMultiSortMeta) {
      return;
    }

    if (hasSingleSortField) {
      incomingSortField = String(evt.sortField ?? '').trim();

      const rawOrder = evt.sortOrder;
      incomingSortOrder = rawOrder === 1 || rawOrder === -1 ? rawOrder : null;
    } else if (hasMultiSortMeta) {
      const firstMeta = evt.multiSortMeta[0];

      incomingSortField = String(firstMeta?.field ?? '').trim();

      const rawOrder = firstMeta?.order;
      incomingSortOrder = rawOrder === 1 || rawOrder === -1 ? rawOrder : null;
    }

    // dedupe identical quick succession events
    const nowMs = Date.now();
    const orderToken = incomingSortOrder == null ? 'null' : String(incomingSortOrder);
    const eventSignature = `${incomingSortField}::${orderToken}`;

    const isSameAsLastEvent = this.lastLazySortEventSignature === eventSignature;
    const happenedVeryRecently = (nowMs - this.lastLazySortEventAtMs) < 250;

    if (isSameAsLastEvent && happenedVeryRecently) {
      return;
    }

    this.lastLazySortEventSignature = eventSignature;
    this.lastLazySortEventAtMs = nowMs;

    const hasValidSortField = incomingSortField.length > 0;
    const hasValidSortOrder = incomingSortOrder === 1 || incomingSortOrder === -1;

    if (!hasValidSortField || !hasValidSortOrder) {
      this.clearSortingState();

      // persist "cleared" state (reset header sort)
      this.persistSortPanelStateToStorage();

      this.pageNumber = 1;
      this.filter.pageNumber = 1;

      this.onCriteriaResetRequested();
      await this.get();

      return;
    }

    const sortDirection: 'ASC' | 'DESC' = incomingSortOrder === 1 ? 'ASC' : 'DESC';

    this.sortPanelSort = {
      key: incomingSortField,
      direction: sortDirection
    };
    this.sortWasClearedByUser = false;

    // Sync table bindings -> icons
    this.primeTableSortField = incomingSortField;
    this.primeTableSortOrder = incomingSortOrder === 1 ? 1 : -1;

    this.applySortToFilterRequest();

    // Persist applied sort (header sort counts as user choice too)
    this.persistSortPanelStateToStorage();

    this.pageNumber = 1;
    this.filter.pageNumber = 1;

    this.onCriteriaResetRequested();
    await this.get();
  }

  public isColumnSortedAsc(columnField: string): boolean {
    const isSameField = this.primeTableSortField === columnField;
    if (!isSameField) {
      return false;
    }

    const ascendingOrder = 1;
    const isAscending = this.primeTableSortOrder === ascendingOrder;

    return isAscending;
  }

  public isColumnSortedDesc(columnField: string): boolean {
    const isSameField = this.primeTableSortField === columnField;
    if (!isSameField) {
      return false;
    }

    const descendingOrder = -1;
    const isDescending = this.primeTableSortOrder === descendingOrder;

    return isDescending;
  }

  public isColumnUnsorted(columnField: string): boolean {
    const isSameField = this.primeTableSortField === columnField;

    const ascendingOrder = 1;
    const descendingOrder = -1;

    const isAscending = this.primeTableSortOrder === ascendingOrder;
    const isDescending = this.primeTableSortOrder === descendingOrder;

    const hasAnySortDirection = isAscending || isDescending;

    if (!isSameField) {
      return true;
    }

    if (!hasAnySortDirection) {
      return true;
    }

    return false;
  }

  protected onCriteriaResetRequested() {
    // Optional hook -> for components that persist paginator state
  }

  async filterEvent(field: string, data: any, operator: string) {
    this.filter.filters ??= [];

    if (!data || data == '') {
      this.filter.filters = this.filter.filters.filter(v => v.propertyName != field);
    } else {
      if (data?.length > 1 && typeof data[0]?.getMonth === 'function') {
        const start = new Date(Date.UTC(data[0].getFullYear(), data[0].getMonth(), data[0].getDate(), 0, 0, 0, 0));
        const end = new Date(Date.UTC(
          (data[1] ?? data[0]).getFullYear(),
          (data[1] ?? data[0]).getMonth(),
          (data[1] ?? data[0]).getDate(),
          23, 59, 59, 999
        ));
        data = [start.toISOString(), end.toISOString()];
      }

      const filter = this.filter.filters.find(v => v.propertyName == field);
      if (filter) {
        filter.values = Array.isArray(data) ? data : [data];
      } else {
        const filterData: Filter = {
          propertyName: field,
          operator: operator,
          values: Array.isArray(data) ? data : [data]
        };
        this.filter.filters.push(filterData);
      }
    }

    this.pageNumber = 1;
    this.filter.pageNumber = 1;

    await this.get();
  }

  onPageChange(data: any) {
    this.pageNumber = data.page;
    this.pageSize = data.rows;
    this.get();
  }

  // Sort Panel storage methods
  private restoreSortPanelStateFromStorage(): void {
    const tableName = this.sortPanelStorageTableName;

    if (!tableName) {
      return;
    }

    const stored = loadStoredSortPanelState(tableName);

    if (!stored) {
      return;
    }

    const storedValues = stored.valuesByFieldKey;

    const wasCleared = storedValues.wasCleared === true;
    if (wasCleared) {
      this.clearSortingState();
      return;
    }

    const rawStoredKey = storedValues.sortKey;
    const storedKey = String(rawStoredKey ?? '').trim();

    const storedDirection = storedValues.sortDirection;

    const hasValidKey = storedKey.length > 0;
    const hasValidDirection = storedDirection === 'ASC' || storedDirection === 'DESC';

    if (!hasValidKey || !hasValidDirection) {
      clearSortPanelState(tableName);
      return;
    }

    // if the column is no longer visible, drop the stored sort
    let isKeyVisible = false;

    for (const option of this.sortableColumns) {
      if (option.key === storedKey) {
        isKeyVisible = true;
        break;
      }
    }

    if (!isKeyVisible) {
      clearSortPanelState(tableName);
      return;
    }

    this.sortPanelSort = {
      key: storedKey,
      direction: storedDirection
    };

    this.sortWasClearedByUser = false;

    this.primeTableSortField = storedKey;

    if (storedDirection === 'ASC') {
      this.primeTableSortOrder = 1;
    } else {
      this.primeTableSortOrder = -1;
    }
  }

  private persistSortPanelStateToStorage(): void {
    const tableName = this.sortPanelStorageTableName;

    if (!tableName) {
      return;
    }

    if (this.sortWasClearedByUser) {
      persistSortPanelState({
        tableName: tableName,
        sortKey: null,
        sortDirection: null,
        wasCleared: true
      });
      return;
    }

    // if user has not explicitly selected a sort yet, don't store anything
    if (!this.sortPanelSort) {
      return;
    }

    persistSortPanelState({
      tableName: tableName,
      sortKey: this.sortPanelSort.key,
      sortDirection: this.sortPanelSort.direction,
      wasCleared: false
    });
  }
}
